import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../Common/designTokens";
import { utcToLocal } from "../../Utils/timeConversions";
import FilterChips from "../InventoryComponents/FilterChips";

const API_TYPE_TO_BOND_TYPE = {
  Gov: "Treasury",
  GOV: "Treasury",
  Muni: "Municipals",
  MUNI: "Municipals",
  Corp: "Corporates",
  CORP: "Corporates",
};

// Build a FilterChips-friendly input from the applied backend payload: drop empty
// values and show the bond type label instead of the raw API "type".
const buildSecMasterChipsInput = (appliedFilters) => {
  if (!appliedFilters) return null;
  const cleaned = {};
  Object.entries(appliedFilters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    cleaned[key] = value;
  });
  if (cleaned.type) {
    cleaned.type = API_TYPE_TO_BOND_TYPE[cleaned.type] || cleaned.type;
  }
  return Object.keys(cleaned).length > 0 ? { ...cleaned, source: "sec-master" } : null;
};

const DownloadMenu = ({ csvLoading, onSelectFormat }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Download">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={csvLoading}
          sx={{
            borderRadius: "8px",
            color: tokens.colors.fontColour,
            width: 42,
            height: 42,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          {csvLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DownloadIcon />
          )}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={close}>
        <MenuItem onClick={() => { close(); onSelectFormat("xlsx"); }}>Download Excel</MenuItem>
        <MenuItem onClick={() => { close(); onSelectFormat("csv"); }}>Download CSV</MenuItem>
      </Menu>
    </>
  );
};

const SecMasterHeader = ({
  totalElements,
  loading,
  lastUpdatedAt,
  csvLoading,
  customizeButtonRef,
  appliedFilters = null,
  onSearch,
  onAddSecurity,
  onClearFilters,
  onDeleteChip,
  onCustomizeClick,
  onDownloadCSV,
}) => {
  const chipsInput = buildSecMasterChipsInput(appliedFilters);

  return (
    <Box
      sx={{
        color: tokens.colors.fontColour,
        padding: "16px",
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", flex: 1, gap: 2, minWidth: 0 }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontSize: { xs: "1rem", sm: "1.25rem" },
              fontWeight: 700,
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
            }}
          >
            Sec Master
            {loading ? (
              <CircularProgress sx={{ ml: 0.8, color: tokens.palette?.primary }} size={20} />
            ) : (
              ` (${totalElements.toLocaleString()})`
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              mt: 0.5,
              whiteSpace: "nowrap",
              color: tokens.colors.fontColour85,
            }}
          >
            Updated at: {utcToLocal(lastUpdatedAt)?.format("DMMMYY HH:mm") ?? "---"}
          </Typography>
        </Box>

        {chipsInput && (
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, mt: 0.5 }}>
            <FilterChips
              input={chipsInput}
              onDeleteChip={onDeleteChip}
              onClearFilters={onClearFilters}
              maxVisibleChips={2}
              labelFormatter={(key) => (key === "searchString" ? "Identifier" : undefined)}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
        <Button
          variant="contained"
          color="inherit"
          startIcon={<AddIcon />}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
          onClick={onAddSecurity}
        >
          Add Security
        </Button>

        <Button
          variant="contained"
          color="inherit"
          startIcon={<FilterAltOutlinedIcon />}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
          onClick={onSearch}
        >
          Search
        </Button>

        <Button
          ref={customizeButtonRef}
          variant="contained"
          color="inherit"
          startIcon={<ViewColumnOutlinedIcon />}
          sx={{
            height: 42,
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: tokens.colors.defaultGridRowColor,
            color: tokens.colors.fontColour,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
          onClick={onCustomizeClick}
        >
          Columns
        </Button>

        <DownloadMenu csvLoading={csvLoading} onSelectFormat={onDownloadCSV} />
      </Box>
    </Box>
  );
};

export default SecMasterHeader;
